(() => {
  const stages = ['barrel', 'points', 'flights', 'case'];
  const esc = value => String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  class LFDCartSetupSave extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-cart-save-config]').textContent);
        this.cart = JSON.parse(this.querySelector('[data-cart-setup-data]').textContent);
      } catch { return; }
      this.button = this.querySelector('[data-cart-save-open]');
      this.status = this.querySelector('[data-cart-save-status]');
      this.events = new AbortController();
      const options = {signal:this.events.signal};
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-cart-save-open')) this.open();
        else if (button.hasAttribute('data-cart-save-group')) this.selectGroup(Number(button.dataset.cartSaveGroup));
        else if (['saved-close','saved-copy','saved-local'].includes(button.dataset.action)) this.sharing?.handle(button.dataset.action, button);
      }, options);
      this.addEventListener('keyup', event => {
        if (event.key === 'Escape' && (this.sharing?.dialog.open || this.escapeClosing)) { event.stopPropagation(); this.escapeClosing = false; }
      }, options);
      document.addEventListener('lfd:cart-updated', event => this.updateVisibility(event.detail?.cart), options);
      window.addEventListener('pageshow', event => { if(event.persisted) this.refreshVisibility(); }, options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshVisibility());
      this.updateVisibility(this.cart);
    }
    disconnectedCallback() {
      this.request?.abort();
      this.visibilityRequest?.abort();
      this.unsubscribe?.();
      this.events?.abort();
      this.events = null;
      if(this.sharing) { this.sharing.dialog.close(); this.sharing.dialog.remove(); this.sharing = null; }
    }
    t(key) { return this.data.copy[key] || key; }
    variantTitle(variant) { return variant?.title === 'Default Title' ? '' : variant?.title || ''; }
    announce(key) { this.status.textContent = this.t(key); }
    groups(cart) {
      const groups = new Map();
      for (const item of cart?.items || []) {
        const id = item.properties?._lfd_setup_id, stage = item.properties?._lfd_stage;
        if(typeof id !== 'string' || !/^[a-zA-Z0-9-]{1,100}$/.test(id) || !stages.includes(stage)) continue;
        if(!Number.isSafeInteger(item.product_id) || !Number.isSafeInteger(item.variant_id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) continue;
        if(!groups.has(id)) groups.set(id, {id, items:[]});
        groups.get(id).items.push(item);
      }
      return [...groups.values()].filter(group => {
        const variants = group.items.map(item => item.variant_id);
        return new Set(variants).size === variants.length && stages.every(stage => {
          const count = group.items.filter(item => item.properties._lfd_stage === stage).length;
          return stage === 'barrel' ? count === 1 : count <= (stage === 'flights' ? 3 : 1);
        });
      });
    }
    updateVisibility(cart) { if(cart) this.hidden = !this.groups(cart).length; }
    async refreshVisibility() {
      this.visibilityRequest?.abort();
      const request = this.visibilityRequest = new AbortController();
      try {
        const root = this.data.root.replace(/\/$/, '') + '/';
        const response = await fetch(root + 'cart.js', {credentials:'same-origin',cache:'no-store',signal:request.signal});
        if(response.ok) this.updateVisibility(await response.json());
      } catch {}
    }
    async open() {
      if(this.button.disabled) return;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy','true');
      this.announce('cart_save_loading');
      this.request = new AbortController();
      try {
        const root = this.data.root.replace(/\/$/, '') + '/';
        const url = new URL(root + 'cart', location.origin);
        url.searchParams.set('section_id','lfd-cart-setup-data');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if(preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id',preview);
        const response = await fetch(url, {credentials:'same-origin',cache:'no-store',signal:this.request.signal});
        if(!response.ok) throw new Error('cart');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.cart = JSON.parse(html.querySelector('[data-cart-setup-data]').textContent);
        if(!this.isConnected) return;
        this.availableGroups = this.groups(this.cart);
        if(!this.availableGroups.length) { this.announce('cart_save_empty'); return; }
        if(!this.sharing) {
          this.sharing = new window.LFDSetupSharing(this);
          this.sharing.dialog.addEventListener('cancel', () => { this.escapeClosing = true; });
          this.sharing.dialog.addEventListener('close', () => this.restoreFocus());
        }
        this.status.textContent = '';
        if(this.availableGroups.length === 1) this.selectGroup(0);
        else {
          this.sharing.dialog.innerHTML = `<h2>${esc(this.t('cart_save_choose'))}</h2><div class="lfd-cart-save-groups">${this.availableGroups.map((group,index) => `<button type="button" class="sq-btn" data-cart-save-group="${index}">${index+1}. ${esc(group.items.filter(item => item.properties._lfd_stage === 'barrel').map(item=>item.title+' · '+this.variantTitle({title:item.variant_title})).join(''))}</button>`).join('')}</div><button type="button" class="sq-link" data-action="saved-close">${esc(this.t('save_close'))}</button>`;
          this.prepareFocus();
          this.sharing.dialog.showModal();
        }
      } catch(error) { if(error.name !== 'AbortError') { this.announce('cart_save_error'); this.restoreFocus(); } }
      finally { this.button.disabled = false; this.button.removeAttribute('aria-busy'); }
    }
    selectGroup(index) {
      const group = this.availableGroups[index];
      if(!group) return;
      try {
        let previous;
        try { previous = JSON.parse(sessionStorage.getItem('lfd-premium-setup-v3')); } catch {}
        this.state = {id:group.id,view:'complete',answers:previous?.id === group.id ? previous.answers : {},selected:{}};
        this.products = [];
        for (const stage of stages) {
          this.state.selected[stage] = group.items.filter(item=>item.properties._lfd_stage===stage).map(item=>({productId:item.product_id,variantId:item.variant_id}));
        }
        for (const item of group.items) {
          let product = this.products.find(product=>product.id===item.product_id);
          if(!product) { product = {id:item.product_id,title:item.title,profile:item.profile || {},variants:[]}; this.products.push(product); }
          product.variants.push({id:item.variant_id,title:item.variant_title});
        }
        if(this.products.some(product=>['shaft','flight'].includes(product.profile.category))) this.state.flightMode = 'separate';
        this.prepareFocus();
        this.sharing.openDialog();
      } catch { this.announce('cart_save_error'); if(!this.sharing.dialog.open) this.restoreFocus(); }
    }
    prepareFocus() {
      const drawer = this.closest('cart-drawer');
      if(drawer && !this.drawerFocusPaused) {
        this.drawerFocusPaused = true;
        drawer.trapFocusSetup = true;
        if(typeof removeTrapFocus === 'function') removeTrapFocus();
      }
    }
    restoreFocus() {
      const drawer = this.closest('cart-drawer');
      if(this.drawerFocusPaused && this.isConnected && drawer?.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(drawer.querySelector('.drawer__inner'), this.button);
      }
      this.drawerFocusPaused = false;
    }
  }
  if(!customElements.get('lfd-cart-setup-save')) customElements.define('lfd-cart-setup-save', LFDCartSetupSave);
})();
